import { Box, Stack, Typography, useMediaQuery } from "@mui/material";
import { OpenEndedAnswerV1 } from "@pulse/pulse-rpcs/lib/types/openEndedAnswerV1";
import { SurveySettingsEnums } from "@pulse/pulse-rpcs/lib/types/surveySettings";
import {
  EMPTY_CHARACTER,
  EMPTY_JSON,
  MAX_OPEN_ENDED_RESPONSE_LIMIT,
  OpenEndedFieldModel,
  RICH_TEXT_DEFAULT_FONT_COLOR,
  RICH_TEXT_MARKDOWN_PROPS,
  createOpenEndedQuestionV1Model,
  richTextTypography,
  useSpeechToTextField,
} from "@pulse/shared-components";
import {
  useCornerRadius,
  useFoundationColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { RichTextEditor } from "@surya-digital/leo-reactjs-remirror";
import { observer } from "mobx-react";
import { Instance } from "mobx-state-tree";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { OpenEndedQuestionV1Model } from "@pulse/shared-components";
import { useTranslation } from "react-i18next";

const DESKTOP_BREAKPOINT = "(min-width:1200px)";
const ANSWER_LINE_HEIGHT_IN_PIXELS = 24;
const COLLAPSED_ANSWER_HEIGHT_IN_PIXELS = ANSWER_LINE_HEIGHT_IN_PIXELS * 4;
const INPUT_MIN_HEIGHT_IN_PIXELS = 120;
const INPUT_MAX_VIEWPORT_HEIGHT = "calc(100dvh - 300px)";

interface ScrollPosition {
  element: HTMLElement;
  scrollTop: number;
}

interface ChatOpenEndedQuestionBodyProps {
  questionDetailsJson: string;
  draftAnswerJson?: string;
  fontFamily: string;
  fontScale: SurveySettingsEnums.FontSizeScale.FontSizeScale;
  isMandatory: boolean;
  onAnswerChange: (answerJson: string | null, isValid: boolean) => void;
}

interface ChatOpenEndedAnswerProps {
  questionDetailsJson: string;
  answerJson: string;
  showMoreText: string;
  showLessText: string;
  skippedAnswerText: string;
}

const isAnswerPresent = (answerJson: string): boolean =>
  answerJson !== EMPTY_JSON;

const createQuestionModel = (
  questionDetailsJson: string,
  answerJson?: string,
): Instance<typeof OpenEndedQuestionV1Model> => {
  const model = createOpenEndedQuestionV1Model();
  model.deserializeQuestionJSON(questionDetailsJson);
  if (answerJson && isAnswerPresent(answerJson)) {
    model.deserializeAnswerJSON(answerJson);
  }
  return model;
};

const ChatOpenEndedField = ({
  field,
  value,
  isMandatory,
  fontFamily,
  fontScale,
  isBigScreen,
  onValueChange,
}: {
  field: Instance<typeof OpenEndedFieldModel>;
  value: string;
  isMandatory: boolean;
  fontFamily: string;
  fontScale: SurveySettingsEnums.FontSizeScale.FontSizeScale;
  isBigScreen: boolean;
  onValueChange: (value: string) => void;
}): React.ReactElement => {
  const spacing = useSpacing();
  const tokens = useFoundationColorTokens();
  const radius = useCornerRadius();
  const typography = useTypography();
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const speechToText = useSpeechToTextField(value, onValueChange);
  const fieldTitle = field.fieldTitle.trim();
  const label =
    fieldTitle === EMPTY_CHARACTER
      ? undefined
      : `${fieldTitle}${isMandatory ? " *" : EMPTY_CHARACTER}`;

  const resizeTextarea = useCallback(
    (preserveScrollPosition: boolean): void => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const scrollPositions: ScrollPosition[] = [];
      if (preserveScrollPosition) {
        const chatScrollContainer = textarea.closest<HTMLElement>(
          "[data-chat-scroll-container]",
        );
        const itemBody = textarea
          .closest("article")
          ?.querySelector<HTMLElement>("[data-chat-item-body]");
        [chatScrollContainer, itemBody].forEach((element) => {
          if (
            element &&
            !scrollPositions.some(
              ({ element: existing }) => existing === element,
            )
          ) {
            scrollPositions.push({ element, scrollTop: element.scrollTop });
          }
        });
      }

      textarea.style.height = "auto";
      const maximumHeight = Math.max(
        INPUT_MIN_HEIGHT_IN_PIXELS,
        Math.min(window.innerHeight - 300, 480),
      );
      const nextHeight = Math.min(textarea.scrollHeight, maximumHeight);
      textarea.style.height = `${nextHeight}px`;
      textarea.style.overflowY =
        textarea.scrollHeight > maximumHeight ? "auto" : "hidden";

      if (scrollPositions.length > 0) {
        requestAnimationFrame(() => {
          scrollPositions.forEach(({ element, scrollTop }) => {
            element.scrollTop = scrollTop;
          });
        });
      }
    },
    [],
  );

  useLayoutEffect(() => {
    resizeTextarea(true);
    const onWindowResize = (): void => resizeTextarea(false);
    window.addEventListener("resize", onWindowResize);
    return (): void => window.removeEventListener("resize", onWindowResize);
  }, [resizeTextarea, speechToText.displayValue]);

  return (
    <Stack gap={spacing.spaceXS} width="100%">
      {label && (
        <Box sx={{ "& .ProseMirror": { overflowWrap: "anywhere" } }}>
          <RichTextEditor
            name={`chatOpenEndedFieldLabel-${field.code}`}
            initialValue={label}
            typography={richTextTypography(
              fontFamily,
              fontScale,
              RICH_TEXT_DEFAULT_FONT_COLOR,
              isBigScreen,
            )}
            mode="preview"
            supports={RICH_TEXT_MARKDOWN_PROPS}
            borderOnPreview={false}
          />
        </Box>
      )}
      <Box
        border={`1px solid ${tokens.border}`}
        borderRadius={radius.radiusXS}
        bgcolor={tokens.backgroundElevatedLevel1}
        sx={{
          overflow: "hidden",
          "&:focus-within": { borderColor: tokens.borderPrimary },
        }}
      >
        <textarea
          ref={textareaRef}
          name={`chatOpenEndedField-${field.code}`}
          value={speechToText.displayValue}
          placeholder={field.fieldHint}
          aria-label={
            fieldTitle ||
            field.fieldHint ||
            t("surveys.chatInterface.openEndedResponseAriaLabel")
          }
          onChange={(event): void =>
            speechToText.onTextChange(
              event.target.value.slice(0, MAX_OPEN_ENDED_RESPONSE_LIMIT),
            )
          }
          style={{
            display: "block",
            resize: "none",
            border: 0,
            outline: 0,
            boxSizing: "border-box",
            background: "transparent",
            color: tokens.label,
            fontFamily: typography.b1.fontFamily,
            fontSize: typography.b1.fontSize,
            lineHeight: `${ANSWER_LINE_HEIGHT_IN_PIXELS}px`,
            minHeight: `${INPUT_MIN_HEIGHT_IN_PIXELS}px`,
            maxHeight: INPUT_MAX_VIEWPORT_HEIGHT,
            width: "100%",
            padding: spacing.spaceSM,
          }}
        />
      </Box>
      {speechToText.footer}
    </Stack>
  );
};

/** Chat-only multiline open-ended response editor. */
export const ChatOpenEndedQuestionBody = observer(
  ({
    questionDetailsJson,
    draftAnswerJson,
    fontFamily,
    fontScale,
    isMandatory,
    onAnswerChange,
  }: ChatOpenEndedQuestionBodyProps): React.ReactElement => {
    const spacing = useSpacing();
    const isBigScreen = useMediaQuery(DESKTOP_BREAKPOINT);
    const model = useMemo(
      () => createQuestionModel(questionDetailsJson, draftAnswerJson),
      [draftAnswerJson, questionDetailsJson],
    );

    const publishAnswer = useCallback((): void => {
      const answerJson = model.serializedAnswerJSON;
      onAnswerChange(
        isAnswerPresent(answerJson) ? answerJson : null,
        !isMandatory || model.validateUserResponse === null,
      );
    }, [isMandatory, model, onAnswerChange]);

    useEffect(() => {
      publishAnswer();
    }, [publishAnswer]);

    return (
      <Stack gap={spacing.spaceLG} width="100%">
        {model.fields.map((field) => (
          <ChatOpenEndedField
            key={field.code}
            field={field}
            value={
              model.getAnswerValueFromAnswerCode(field.code) ?? EMPTY_CHARACTER
            }
            isMandatory={isMandatory}
            fontFamily={fontFamily}
            fontScale={fontScale}
            isBigScreen={isBigScreen}
            onValueChange={(value): void => {
              model.setFieldValue(value, field.code);
              publishAnswer();
            }}
          />
        ))}
      </Stack>
    );
  },
);

export const ChatOpenEndedAnswer = ({
  questionDetailsJson,
  answerJson,
  showMoreText,
  showLessText,
  skippedAnswerText,
}: ChatOpenEndedAnswerProps): React.ReactElement => {
  const spacing = useSpacing();
  const typography = useTypography();
  const contentRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const model = useMemo(
    () => createQuestionModel(questionDetailsJson),
    [questionDetailsJson],
  );
  const answer = useMemo(
    () => OpenEndedAnswerV1.fromDTO(JSON.parse(answerJson)),
    [answerJson],
  );
  const answersByFieldCode = new Map(
    answer.fieldAnswers.map((fieldAnswer) => [
      fieldAnswer.code.code,
      fieldAnswer.answer,
    ]),
  );
  const fields = model.fields.map((field) => ({
    code: field.code,
    title: field.fieldTitle.trim(),
    answer: answersByFieldCode.get(field.code) ?? EMPTY_CHARACTER,
  }));
  const shouldShowFieldTitles = fields.length > 1;

  const checkOverflow = useCallback((): void => {
    const content = contentRef.current;
    if (!content) return;
    setIsOverflowing(
      content.scrollHeight > COLLAPSED_ANSWER_HEIGHT_IN_PIXELS + 1,
    );
  }, []);

  useLayoutEffect(() => {
    checkOverflow();
    const content = contentRef.current;
    if (!content) return undefined;
    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(content);
    return (): void => resizeObserver.disconnect();
  }, [checkOverflow, fields.length, answerJson]);

  return (
    <Stack alignItems="flex-start" gap={spacing.spaceXS}>
      <Box
        ref={contentRef}
        width="100%"
        sx={{
          maxHeight: isExpanded
            ? undefined
            : `${COLLAPSED_ANSWER_HEIGHT_IN_PIXELS}px`,
          overflow: "hidden",
        }}
      >
        <Stack gap={spacing.spaceSM}>
          {fields.map((field) => (
            <Stack key={field.code} gap={spacing.spaceXS}>
              {shouldShowFieldTitles && field.title && (
                <Typography sx={typography.b2}>{field.title}</Typography>
              )}
              <Typography
                sx={{
                  ...typography.b1,
                  lineHeight: `${ANSWER_LINE_HEIGHT_IN_PIXELS}px`,
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                }}
              >
                {field.answer.trim() === EMPTY_CHARACTER
                  ? skippedAnswerText
                  : field.answer}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>
      {isOverflowing && (
        <Box
          component="button"
          type="button"
          onClick={(): void => setIsExpanded((expanded) => !expanded)}
          sx={{
            border: 0,
            padding: 0,
            background: "transparent",
            color: "inherit",
            font: "inherit",
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          {isExpanded ? showLessText : showMoreText}
        </Box>
      )}
    </Stack>
  );
};
